/**
 * Shared Utilities Module
 * Common functionality used across multiple systems
 */

// DOM utilities
export const dom = {
  /**
   * Query selector with error handling
   * @param {string} selector 
   * @param {Element} parent 
   * @returns {Element|null}
   */
  $(selector, parent = document) {
    try {
      return parent.querySelector(selector);
    } catch (error) {
      console.warn(`Invalid selector: ${selector}`);
      return null;
    }
  },

  /**
   * Query all with error handling
   * @param {string} selector 
   * @param {Element} parent 
   * @returns {NodeList}
   */
  $$(selector, parent = document) {
    try {
      return parent.querySelectorAll(selector);
    } catch (error) {
      console.warn(`Invalid selector: ${selector}`);
      return [];
    }
  },

  /**
   * Create element with attributes
   * @param {string} tag 
   * @param {Object} attributes 
   * @param {string} content 
   * @returns {Element}
   */
  create(tag, attributes = {}, content = '') {
    const element = document.createElement(tag);
    
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'className') {
        element.className = value;
      } else if (key === 'dataset') {
        Object.entries(value).forEach(([dataKey, dataValue]) => {
          element.dataset[dataKey] = dataValue;
        });
      } else {
        element.setAttribute(key, value);
      }
    });
    
    if (content) {
      element.innerHTML = content;
    }
    
    return element;
  },

  /**
   * Check if element is visible
   * @param {Element} element 
   * @returns {boolean}
   */
  isVisible(element) {
    return element && element.offsetParent !== null;
  },

  /**
   * Get element position relative to viewport
   * @param {Element} element 
   * @returns {Object}
   */
  getPosition(element) {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height
    };
  }
};

// Event utilities
export const events = {
  /**
   * Debounce function calls
   * @param {Function} func 
   * @param {number} wait 
   * @returns {Function}
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  /**
   * Throttle function calls
   * @param {Function} func 
   * @param {number} limit 
   * @returns {Function}
   */
  throttle(func, limit) {
    let inThrottle;
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  /**
   * Add event listener with cleanup
   * @param {Element} element 
   * @param {string} event 
   * @param {Function} handler 
   * @param {Object} options 
   * @returns {Function} cleanup function
   */
  on(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    return () => element.removeEventListener(event, handler, options);
  },

  /**
   * Dispatch custom event
   * @param {Element} element 
   * @param {string} eventName 
   * @param {any} detail 
   */
  emit(element, eventName, detail = null) {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true
    });
    element.dispatchEvent(event);
  }
};

// Storage utilities
export const storage = {
  /**
   * Get item from localStorage with JSON parsing
   * @param {string} key 
   * @param {any} defaultValue 
   * @returns {any}
   */
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Failed to get ${key} from storage:`, error);
      return defaultValue;
    }
  },

  /**
   * Set item in localStorage with JSON stringification
   * @param {string} key 
   * @param {any} value 
   */
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to set ${key} in storage:`, error);
    }
  },

  /**
   * Remove item from localStorage
   * @param {string} key 
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove ${key} from storage:`, error);
    }
  },

  /**
   * Clear all localStorage
   */
  clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn('Failed to clear storage:', error);
    }
  }
};

// Validation utilities
export const validate = {
  /**
   * Check if value is empty
   * @param {any} value 
   * @returns {boolean}
   */
  isEmpty(value) {
    return value === null || value === undefined || value === '' || 
           (Array.isArray(value) && value.length === 0) ||
           (typeof value === 'object' && Object.keys(value).length === 0);
  },

  /**
   * Validate email format
   * @param {string} email 
   * @returns {boolean}
   */
  isEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate phone number
   * @param {string} phone 
   * @returns {boolean}
   */
  isPhone(phone) {
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
  },

  /**
   * Validate required fields
   * @param {Object} data 
   * @param {string[]} requiredFields 
   * @returns {Object}
   */
  required(data, requiredFields) {
    const errors = {};
    requiredFields.forEach(field => {
      if (this.isEmpty(data[field])) {
        errors[field] = `${field} is required`;
      }
    });
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

// Format utilities
export const format = {
  /**
   * Format money with currency
   * @param {number} amount 
   * @param {string} currency 
   * @returns {string}
   */
  money(amount, currency = null) {
    try {
      // Get currency from Shopify context if not provided
      if (!currency) {
        currency = window.Shopify?.currency?.active || 
                  window.Shopify?.shop?.currency || 
                  document.documentElement.getAttribute('data-currency') ||
                  'USD';
      }
      
      // Get locale from Shopify context or use browser default
      const locale = window.Shopify?.locale || 
                    document.documentElement.getAttribute('data-locale') ||
                    navigator.language ||
                    'en-US';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amount / 100);
    } catch (error) {
      // Fallback: try with USD if currency fails
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(amount / 100);
      } catch (fallbackError) {
        return `$${(amount / 100).toFixed(2)}`;
      }
    }
  },

  /**
   * Format number with thousands separator
   * @param {number} number 
   * @returns {string}
   */
  number(number) {
    return new Intl.NumberFormat().format(number);
  },

  /**
   * Format date
   * @param {Date|string} date 
   * @param {Object} options 
   * @returns {string}
   */
  date(date, options = {}) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options })
      .format(dateObj);
  },

  /**
   * Truncate text with ellipsis
   * @param {string} text 
   * @param {number} maxLength 
   * @returns {string}
   */
  truncate(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength - 3) + '...';
  },

  /**
   * Capitalize first letter
   * @param {string} text 
   * @returns {string}
   */
  capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
};

// HTTP utilities
export const http = {
  /**
   * Make HTTP request with error handling
   * @param {string} url 
   * @param {Object} options 
   * @returns {Promise}
   */
  async request(url, options = {}) {
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };

    const config = { ...defaultOptions, ...options };
    
    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      
      return await response.text();
    } catch (error) {
      console.error('HTTP request failed:', error);
      throw error;
    }
  },

  /**
   * GET request
   * @param {string} url 
   * @param {Object} options 
   * @returns {Promise}
   */
  get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  },

  /**
   * POST request
   * @param {string} url 
   * @param {any} data 
   * @param {Object} options 
   * @returns {Promise}
   */
  post(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
};

// Performance utilities
export const perf = {
  /**
   * Measure execution time
   * @param {Function} func 
   * @param {string} label 
   * @returns {any}
   */
  async measure(func, label = 'Operation') {
    const start = performance.now();
    const result = await func();
    const end = performance.now();
    console.log(`${label} took ${(end - start).toFixed(2)}ms`);
    return result;
  },

  /**
   * Request animation frame promise
   * @returns {Promise}
   */
  nextFrame() {
    return new Promise(resolve => requestAnimationFrame(resolve));
  },

  /**
   * Request idle callback promise
   * @param {number} timeout 
   * @returns {Promise}
   */
  nextIdle(timeout = 5000) {
    return new Promise(resolve => {
      if (window.requestIdleCallback) {
        requestIdleCallback(resolve, { timeout });
      } else {
        setTimeout(resolve, 0);
      }
    });
  }
};

// Export all utilities as default
export default {
  dom,
  events,
  storage,
  validate,
  format,
  http,
  perf
}; 